import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Globe, ArrowRight, Activity, ShieldAlert,
  Clock, CheckCircle,
  Download, Trash2, ChevronRight, ChevronDown, ListTodo,
  Sparkles, Gauge, Compass, Link2, Copy, Check, Info, FileSpreadsheet, FileText, FileCode,
  Network, Braces, Image as ImageIcon, Crosshair, SearchCheck, TrendingUp, X
} from 'lucide-react';
import LinkGraphPanel from './LinkGraphPanel';
import SchemaValidatorPanel from './SchemaValidatorPanel';
import ImagesPanel from './ImagesPanel';
import KeywordConflictsPanel from './KeywordConflictsPanel';
import SerpPreviewPanel from './SerpPreviewPanel';
import TrendsPanel from './TrendsPanel';
import AiMetaGenerator from './AiMetaGenerator';
import AiContentOutline from './AiContentOutline';
import AiFixesPanel from './AiFixesPanel';

const API_URL = process.env.REACT_APP_API_URL || 'https://rnd.kalptree.xyz/api';

const loadingStages = [
  'Initializing pipeline & validating host...',
  'Fetching robots.txt and sitemap files...',
  'Crawling domain frontier and sampling site pages...',
  'Performing Technical SEO validation & checking tags...',
  'Measuring Page Speed & Mobile Readiness metrics...',
  'Querying LLM engine for content strategies & target keywords...',
  'Consolidating insights and compiling final report...'
];

const SEODashboard = () => {
  const [urlInput, setUrlInput] = useState('');
  const [businessType, setBusinessType] = useState('General');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPageFilter, setSelectedPageFilter] = useState('ALL');
  const [visibleHeadingLevels, setVisibleHeadingLevels] = useState({ H1: true, H2: true, H3: true, H4: true });
  const [expandedFinding, setExpandedFinding] = useState(null);
  const [generatingFixId, setGeneratingFixId] = useState(null);
  const [fixes, setFixes] = useState({});
  const [copiedFixId, setCopiedFixId] = useState(null);
  const [notification, setNotification] = useState(null);

  const [customCompetitorsInput, setCustomCompetitorsInput] = useState('');
  const [customCompetitorsList, setCustomCompetitorsList] = useState([]);
  const [discoveredCandidates, setDiscoveredCandidates] = useState([]);
  const [discoveringCompetitors, setDiscoveringCompetitors] = useState(false);
  const [quickFixModal, setQuickFixModal] = useState(null);
  const [copiedModalCode, setCopiedModalCode] = useState(false);
  const [isApplyingFix, setIsApplyingFix] = useState(false);

  const showNotification = useCallback((msg, type = 'info') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);


  const handleDiscoverCompetitors = async (targetUrl, overrideRegion) => {
    if (!targetUrl || !targetUrl.trim()) return;
    let formattedUrl = targetUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = `https://${formattedUrl}`;
    }

    const regToUse = overrideRegion !== undefined ? overrideRegion : selectedRegion;

    setDiscoveringCompetitors(true);
    try {
      const res = await apiFetch('/seo/discover-competitors', {
        method: 'POST',
        body: JSON.stringify({ url: formattedUrl, region: regToUse || undefined })
      });
      if (res && res.candidates && res.candidates.length > 0) {
        setDiscoveredCandidates(res.candidates);
      }
    } catch (err) {
      console.warn('Auto competitor discovery failed:', err);
    } finally {
      setDiscoveringCompetitors(false);
    }
  };

  const handleAddCustomCompetitor = (e) => {
    e?.preventDefault();
    const val = customCompetitorsInput.trim();
    if (!val) return;
    const cleanUrl = val.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').toLowerCase();
    const formatted = `https://${cleanUrl}`;
    if (!customCompetitorsList.includes(formatted)) {
      setCustomCompetitorsList(prev => [...prev, formatted]);
    }
    setCustomCompetitorsInput('');
  };

  const handleRemoveCustomCompetitor = (comp) => {
    setCustomCompetitorsList(prev => prev.filter(c => c !== comp));
  };

  const handleOpenQuickFix = (type) => {
    const domain = selectedProject?.url?.replace(/\/$/, '') || 'https://hedcon.kalptree.xyz';
    if (type === 'robots') {
      setQuickFixModal({
        type: 'robots',
        title: 'Generate Production-Ready robots.txt',
        filename: 'robots.txt',
        fileLocation: 'public/robots.txt',
        code: `# Standard robots.txt for ${domain}\nUser-agent: *\nAllow: /\n\n# XML Sitemap Link\nSitemap: ${domain}/sitemap.xml\n`,
        explanation: 'This robots.txt informs search engine crawlers (Googlebot, Bingbot) that the website is open for indexing and directs them to your live XML sitemap.'
      });
    } else if (type === 'sitemap') {
      const sampledPages = selectedProject?.crawl_overview?.sampled_pages || [];
      const discoveredUrls = sampledPages.length > 0
        ? sampledPages.map(p => p.url)
        : [domain];
      const uniqueUrls = Array.from(new Set([domain, ...discoveredUrls]));
      const today = new Date().toISOString().split('T')[0];
      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${uniqueUrls.map((u, i) => `  <url>
    <loc>${u}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>${i === 0 ? 'daily' : 'weekly'}</changefreq>
    <priority>${i === 0 ? '1.0' : '0.8'}</priority>
  </url>`).join('\n')}
</urlset>`;

      setQuickFixModal({
        type: 'sitemap',
        title: 'Generate Production XML Sitemap',
        filename: 'sitemap.xml',
        fileLocation: 'public/sitemap.xml',
        code: xml,
        explanation: `This XML sitemap lists ${uniqueUrls.length} verified site pages discovered during the crawl to ensure complete search engine indexation.`
      });
    }
  };

  const handleDownloadQuickFix = () => {
    if (!quickFixModal) return;
    const blob = new Blob([quickFixModal.code], { type: quickFixModal.type === 'robots' ? 'text/plain' : 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = quickFixModal.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showNotification(`Downloaded ${quickFixModal.filename}! Save to your site's public/ directory.`, 'success');
  };

  const handleApplyQuickFixToWebsite = async () => {
    if (!quickFixModal || !selectedProject) return;
    setIsApplyingFix(true);
    try {
      const fixType = quickFixModal.type === 'robots' ? 'robots_txt' : 'sitemap_xml';
      const payload = {
        project_url: selectedProject.url,
        fix_type: fixType,
        file_path: quickFixModal.fileLocation,
        content: quickFixModal.code
      };
      await apiFetch('/seo/apply-fix', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setSelectedProject(prev => {
        if (!prev) return prev;
        const updated = { ...prev };
        if (fixType === 'robots_txt') {
          updated.robots_status = 'Valid / Present (Auto-Applied)';
          if (updated.crawl_overview) {
            updated.crawl_overview = { ...updated.crawl_overview, robots_txt_status: 'Valid / Present (Auto-Applied)' };
          }
        } else if (fixType === 'sitemap_xml') {
          updated.sitemap_status = 'Valid / Present (Auto-Applied)';
          if (updated.crawl_overview) {
            updated.crawl_overview = { ...updated.crawl_overview, sitemap_status: 'Valid / Present (Auto-Applied)' };
          }
        }
        updated.overall_score = Math.min(100, (updated.overall_score || 65) + 8);
        return updated;
      });

      showNotification(`🎉 ${quickFixModal.filename} successfully applied and deployed to ${selectedProject.url}!`, 'success');
      setQuickFixModal(null);
    } catch (err) {
      showNotification('Could not auto-apply fix: ' + err.message, 'error');
    } finally {
      setIsApplyingFix(false);
    }
  };


  const apiFetch = useCallback(async (path, options = {}) => {
    let cleanPath = path;
    const cleanBase = API_URL.replace(/\/+$/, '');
    if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api/')) {
      cleanPath = cleanPath.replace(/^\/api/, '');
    }
    const url = `${cleanBase}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`;
    const defaultHeaders = {};
    if (options.body && !(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, {
      ...options,
      headers: { ...defaultHeaders, ...options.headers }
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || data.message || 'Request failed');
    }
    return data;
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const data = await apiFetch('/seo/projects');
      setProjects(data || []);
    } catch (err) {
      console.error('Failed to load SEO projects:', err);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!urlInput.trim()) return;

    let formattedUrl = urlInput.trim();
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = 'https://' + formattedUrl;
    }

    setLoading(true);
    setSelectedProject(null);
    setFixes({});
    setExpandedFinding(null);

    try {
      const payload = {
        url: formattedUrl,
        business_type: businessType,
        competitors: customCompetitorsList,
        region: selectedRegion || undefined
      };


      const result = await apiFetch('/seo/analyze', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setSelectedProject(result);
      showNotification('SEO Crawl and Analysis Complete!', 'success');
      loadProjects();
    } catch (err) {
      showNotification(err.message || 'Failed to complete SEO analysis.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = async (project) => {
    try {
      const detailedProject = await apiFetch(`/seo/projects/${project.id}`);
      setSelectedProject(detailedProject);
      setFixes({});
      setExpandedFinding(null);
      setActiveTab('overview');
    } catch (err) {
      showNotification('Failed to load project details.', 'error');
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this SEO project history?')) return;

    try {
      await apiFetch(`/seo/projects/${projectId}`, { method: 'DELETE' });
      showNotification('Project deleted successfully.');
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
      loadProjects();
    } catch (err) {
      showNotification('Failed to delete project.', 'error');
    }
  };

  const handleGenerateFix = async (finding, index) => {
    setGeneratingFixId(index);
    try {
      const evidenceUrl = finding.evidence?.[0]?.url || selectedProject?.url;
      const sampledPage = selectedProject?.crawl_overview?.sampled_pages?.find(
        (p) => p.url === evidenceUrl
      ) || selectedProject?.crawl_overview?.sampled_pages?.[0];

      const payload = {
        finding: `${finding.category} issue: ${finding.metric}. Recommendation: ${finding.recommendation}`,
        url: evidenceUrl,
        page_info: sampledPage ? {
          title: sampledPage.page_info?.title,
          meta_description: sampledPage.page_info?.meta_description,
          canonical: sampledPage.page_info?.canonical,
          h1_content: sampledPage.headings?.h1_content,
          h1_count: sampledPage.headings?.h1_count
        } : null
      };

      const result = await apiFetch('/seo/generate-fix', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      setFixes(prev => ({
        ...prev,
        [index]: result
      }));
    } catch (err) {
      showNotification('Could not generate AI fix: ' + err.message, 'error');
    } finally {
      setGeneratingFixId(null);
    }
  };

  const handleCopyCode = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedFixId(index);
    setTimeout(() => setCopiedFixId(null), 2000);
  };

  const handleDownloadPDF = () => {
    if (!selectedProject?.report_url) return;
    const downloadUrl = `${API_URL}${selectedProject.report_url}`;
    window.open(downloadUrl, '_blank');
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'text-red-400 bg-red-950/40 border-red-500/30';
      case 'medium':
        return 'text-yellow-400 bg-yellow-950/40 border-yellow-500/30';
      case 'low':
        return 'text-blue-400 bg-blue-950/40 border-blue-500/30';
      default:
        return 'text-gray-400 bg-gray-950/40 border-gray-500/30';
    }
  };

  return (
    <div className="min-h-[600px] text-gray-200 grid grid-cols-1 lg:grid-cols-12 gap-6 animate-in fade-in duration-300">

      {/* Toast Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-lg border shadow-xl flex items-center gap-2 ${notification.type === 'error'
              ? 'bg-red-950/90 border-red-700 text-red-200'
              : 'bg-green-950/90 border-green-700 text-green-200'
              }`}
          >
            {notification.type === 'error' ? <ShieldAlert size={16} /> : <CheckCircle size={16} />}
            <span className="text-sm font-medium">{notification.msg || notification.message}</span>

          </motion.div>
        )}
      </AnimatePresence>

      {/* LEFT: Project History Sidebar (3 columns) */}
      <div className="lg:col-span-3 bg-gray-900 border border-gray-800 rounded-2xl p-4 flex flex-col shrink-0 h-[fit-content] max-h-[850px] overflow-y-auto custom-scrollbar">
        <div className="flex items-center justify-between mb-4 border-b border-gray-800 pb-3">
          <h3 className="font-bold text-gray-300 text-sm flex items-center gap-2">
            <Clock size={16} className="text-blue-400" />
            Audit History
          </h3>
          <span className="text-xs text-gray-500 font-mono bg-gray-950 px-2 py-0.5 rounded-full">
            {projects.length}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          {projects.length === 0 ? (
            <div className="text-center py-8 text-gray-600 text-sm">
              No websites analyzed yet.
            </div>
          ) : (
            projects.map((p) => {
              const isActive = selectedProject?.id === p.id;
              return (
                <div
                  key={p.id}
                  onClick={() => handleSelectProject(p)}
                  className={`p-3 rounded-xl border transition-all cursor-pointer group flex flex-col gap-1.5 relative overflow-hidden ${isActive
                    ? 'bg-blue-600/10 border-blue-500 shadow-md shadow-blue-900/10'
                    : 'bg-gray-950 border-gray-800 hover:border-gray-700'
                    }`}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-xs truncate max-w-[140px] text-gray-300 group-hover:text-white transition-colors" title={p.url}>
                      {p.url.replace(/^https?:\/\//i, '')}
                    </span>
                    <button
                      onClick={(e) => handleDeleteProject(e, p.id)}
                      className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 p-0.5 rounded transition-opacity"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-500">
                    <span className="bg-gray-900 px-2.5 py-0.5 rounded-full border border-gray-800 text-gray-400 font-medium capitalize">
                      {p.business_type || 'General'}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-400 font-mono">Score:</span>
                      <span className={`font-mono font-bold ${(p.seo_score || 0) >= 80 ? 'text-green-400' : (p.seo_score || 0) >= 50 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                        {p.seo_score ?? 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT: Dashboard Controls and Results (9 columns) */}
      <div className="lg:col-span-9 flex flex-col gap-6">

        {/* Audit Search Trigger */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl pointer-events-none"></div>

          <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
            <Search className="text-blue-500 w-5 h-5 animate-pulse" />
            AI SEO Web Auditor
          </h2>
          <p className="text-xs text-gray-400 mb-6 max-w-xl">
            Crawl and audit any website's technical SEO parameters, page speed metrics, internal linking hierarchy, and generate full AI keywords content roadmaps.
          </p>

          <form onSubmit={handleAnalyze} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Enter site domain (e.g. www.mybusiness.com)"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onBlur={() => handleDiscoverCompetitors(urlInput)}
                disabled={loading}
                className="w-full bg-black border border-gray-800 rounded-xl py-3 pl-12 pr-4 text-sm text-gray-200 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all placeholder-gray-600 disabled:opacity-50"
              />
            </div>

            <div className="w-full sm:w-44 shrink-0">
              <select
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                disabled={loading}
                className="w-full bg-black border border-gray-800 rounded-xl py-3 px-4 text-sm text-gray-300 outline-none focus:border-blue-500 transition-all disabled:opacity-50"
              >
                <option value="General">General / Agency</option>
                <option value="E-commerce">E-Commerce Store</option>
                <option value="SaaS">SaaS Platform</option>
                <option value="Local Business">Local Business</option>
                <option value="Blog">Blog / Publisher</option>
              </select>
            </div>

            <div className="w-full sm:w-44 shrink-0">
              <select
                value={selectedRegion}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedRegion(val);
                  if (urlInput.trim()) {
                    handleDiscoverCompetitors(urlInput, val);
                  }
                }}
                disabled={loading}
                className="w-full bg-black border border-gray-800 rounded-xl py-3 px-4 text-sm text-gray-300 outline-none focus:border-blue-500 transition-all disabled:opacity-50 cursor-pointer"
              >
                <option value="">Region: Auto-Detect</option>
                <option value="India">India 🇮🇳</option>
                <option value="United States">United States 🇺🇸</option>
                <option value="United Kingdom">United Kingdom 🇬🇧</option>
                <option value="Canada">Canada 🇨🇦</option>
                <option value="Australia">Australia 🇦🇺</option>
                <option value="Germany">Germany 🇩🇪</option>
                <option value="France">France 🇫🇷</option>
                <option value="Japan">Japan 🇯🇵</option>
                <option value="United Arab Emirates">UAE 🇦🇪</option>
                <option value="Singapore">Singapore 🇸🇬</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading || !urlInput.trim()}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold px-6 py-3 rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shrink-0 hover:shadow-blue-900/20"
            >
              Analyze Site <ArrowRight size={16} />
            </button>
          </form>

          {/* INSTANT COMPETITOR DISCOVERY & SELECTION IN INITIAL STAGE */}
          <div className="mt-4 pt-4 border-t border-gray-850 flex flex-col gap-2.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <Globe size={13} className="text-purple-400" /> Select Competitors to Evaluate:
              </label>
              <button
                type="button"
                onClick={() => handleDiscoverCompetitors(urlInput)}
                disabled={!urlInput.trim() || discoveringCompetitors}
                className="text-[10px] text-purple-400 hover:text-purple-300 font-mono font-bold bg-purple-950/40 border border-purple-500/30 px-2 py-0.5 rounded transition disabled:opacity-40"
              >
                {discoveringCompetitors ? '🔍 Finding Competitors...' : '⚡ Auto-Discover Competitors'}
              </button>
            </div>

            {/* Candidate checkboxes discovered before crawl */}
            {discoveredCandidates.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {discoveredCandidates.map((candUrl, idx) => {
                  const domainStr = candUrl.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
                  const isSelected = customCompetitorsList.includes(candUrl);

                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          setCustomCompetitorsList(prev => prev.filter(c => c !== candUrl));
                        } else {
                          if (customCompetitorsList.length < 3) {
                            setCustomCompetitorsList(prev => [...prev, candUrl]);
                          }
                        }
                      }}
                      className={`px-3 py-1.5 rounded-xl text-xs font-mono font-semibold transition-all flex items-center gap-1.5 border ${isSelected
                        ? 'bg-purple-950/90 border-purple-500/70 text-purple-200 shadow-md shadow-purple-950'
                        : 'bg-black/60 border-gray-800 text-gray-400 hover:border-gray-700'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => { }}
                        className="w-3.5 h-3.5 rounded accent-purple-600 cursor-pointer"
                      />
                      <span>🌐 {domainStr}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Custom competitor manual write-in */}
            <div className="flex items-center gap-2 mt-2">
              <input
                type="text"
                placeholder="Or add custom domain (e.g. competitor.com)"
                value={customCompetitorsInput}
                onChange={(e) => setCustomCompetitorsInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomCompetitor())}
                disabled={loading || customCompetitorsList.length >= 3}
                className="flex-1 bg-black/60 border border-gray-800 rounded-xl py-2 px-3.5 text-xs text-purple-300 font-mono outline-none focus:border-purple-500 placeholder-gray-600 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={handleAddCustomCompetitor}
                disabled={loading || !customCompetitorsInput.trim() || customCompetitorsList.length >= 3}
                className="bg-purple-950/80 hover:bg-purple-900 border border-purple-500/30 text-purple-200 text-xs font-bold px-3.5 py-2 rounded-xl transition disabled:opacity-40 shrink-0"
              >
                + Add Domain
              </button>
            </div>
          </div>
        </div>



        {/* LOADING ANIMATION */}
        {loading && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 shadow-xl flex flex-col items-center justify-center min-h-[400px]">
            <div className="relative w-32 h-32 mb-8">
              <div className="absolute inset-0 rounded-full border-4 border-blue-500/10"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
              <div className="absolute inset-4 rounded-full border border-purple-500/20 animate-pulse"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Globe size={32} className="text-blue-400 animate-pulse" />
              </div>
            </div>

            <h3 className="text-lg font-bold text-white mb-2 animate-pulse">Crawl Engine Running...</h3>
            <p className="text-xs text-gray-500 font-mono max-w-md text-center h-8">
              {loadingStages[loadingStage]}
            </p>
          </div>
        )}

        {/* NO SELECTED PROJECT MESSAGE */}
        {!loading && !selectedProject && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 shadow-xl flex flex-col items-center justify-center text-center min-h-[300px]">
            <Compass size={48} className="text-gray-700 mb-4 animate-bounce" />
            <h3 className="text-lg font-bold text-gray-300 mb-1">No Site Analysis Active</h3>
            <p className="text-sm text-gray-500 max-w-sm">
              Please enter a website URL above to start a crawl audit or select an existing project from the history list to view past reports.
            </p>
          </div>
        )}

        {/* DETAILED RESULTS DASHBOARD */}
        {!loading && selectedProject && (
          <div className="flex flex-col gap-6 animate-in fade-in duration-500">

            {/* Project Header Banner */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-blue-600"></div>

              <div className="flex items-center gap-4">
                <div className="bg-blue-600/15 border border-blue-500/20 p-4 rounded-2xl text-blue-400 shrink-0 shadow-inner">
                  <Globe size={28} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-extrabold text-white truncate max-w-[280px] sm:max-w-md">
                      {selectedProject.url.replace(/^https?:\/\//i, '')}
                    </h3>
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-gray-950 border border-gray-800 rounded text-gray-400">
                      {selectedProject.business_type || 'General'}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 font-mono mt-1">
                    Analyzed on: {selectedProject.created_at ? new Date(selectedProject.created_at).toLocaleString() : 'Just now'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 shrink-0 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 border-gray-800 pt-4 md:pt-0">
                {/* Visual score wheel */}
                <div className="flex items-center gap-3">
                  <div className="relative w-14 h-14">
                    <svg className="w-full h-full transform -rotate-95" viewBox="0 0 36 36">
                      <path
                        className="text-gray-800"
                        strokeWidth="3"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className={(selectedProject.overall_score || 0) >= 80 ? 'text-green-500' : (selectedProject.overall_score || 0) >= 50 ? 'text-yellow-500' : 'text-red-500'}
                        strokeWidth="3.2"
                        strokeDasharray={`${selectedProject.overall_score || 0}, 100`}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-black font-mono text-white">{selectedProject.overall_score ?? 0}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 uppercase tracking-widest block font-bold">On-Page SEO Score</span>
                    <span className={`text-xs font-bold font-mono ${(selectedProject.overall_score || 0) >= 80 ? 'text-green-400' : (selectedProject.overall_score || 0) >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                      On-Page SEO: {selectedProject.overall_score ?? selectedProject.onpage_score ?? 68}%
                    </span>
                  </div>
                </div>

                {selectedProject.report_url && (
                  <button
                    onClick={handleDownloadPDF}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold py-2.5 px-4 rounded-xl transition shadow-lg shadow-blue-900/10"
                  >
                    <Download size={14} /> Report PDF
                  </button>
                )}
              </div>
            </div>

            {/* TAB CONTAINER */}
            <div className="flex border-b border-gray-800 bg-gray-900/40 p-1 rounded-xl w-full overflow-x-auto gap-1 custom-scrollbar shrink-0">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'overview' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <Activity size={14} /> Executive Summary
              </button>
              <button
                onClick={() => setActiveTab('findings')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'findings' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <ShieldAlert size={14} /> Technical Audit ({selectedProject.findings_count ?? selectedProject.technical_audit?.findings?.length ?? 0})
              </button>
              <button
                onClick={() => setActiveTab('onpage')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'onpage' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <FileCode size={14} /> On-Page SEO
              </button>
              <button
                onClick={() => setActiveTab('content')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'content' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <Sparkles size={14} /> Keywords & Strategy
              </button>
              <button
                onClick={() => setActiveTab('performance')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'performance' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <Gauge size={14} /> Performance & Speed
              </button>
              <button
                onClick={() => setActiveTab('roadmap')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'roadmap' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <ListTodo size={14} /> Recommended Roadmap
              </button>
              <button
                onClick={() => setActiveTab('linkgraph')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'linkgraph' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <Network size={14} /> Link Graph
              </button>
              <button
                onClick={() => setActiveTab('schema')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'schema' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <Braces size={14} /> Schema Validator
              </button>
              <button
                onClick={() => setActiveTab('images')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'images' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <ImageIcon size={14} /> Images & Assets
              </button>
              <button
                onClick={() => setActiveTab('conflicts')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'conflicts' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <Crosshair size={14} /> Keyword Conflicts
              </button>
              <button
                onClick={() => setActiveTab('serp')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'serp' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <SearchCheck size={14} /> SERP Preview
              </button>
              <button
                onClick={() => setActiveTab('trends')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'trends' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}
              >
                <TrendingUp size={14} /> Score Trends
              </button>
              <button
                onClick={() => setActiveTab('aifixes')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all shrink-0 ${activeTab === 'aifixes' ? 'bg-purple-600 text-white shadow-md shadow-purple-900/30' : 'text-purple-400 hover:text-purple-200 hover:bg-purple-950/40'
                  }`}
              >
                <Sparkles size={14} className="text-purple-400 animate-pulse" /> AI Code Fixes
              </button>
            </div>

            {/* PAGE FILTER DROPDOWN BAR */}
            {(() => {
              const pagesList = (
                selectedProject.technical_audit?.page_summaries?.length ? selectedProject.technical_audit.page_summaries :
                  (selectedProject.crawl_overview?.sampled_pages?.length ? selectedProject.crawl_overview.sampled_pages : [])
              );

              // Fallback to evidence URLs if page_summaries is empty
              const fallbackUrls = pagesList.length ? pagesList : Array.from(new Set(
                (selectedProject.technical_audit?.findings || [])
                  .flatMap(f => f.evidence || [])
                  .map(e => e.url)
                  .filter(Boolean)
              )).map(url => ({ url, page_info: { title: url.replace(selectedProject.url || '', '') || '/' } }));

              return (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-3.5 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Globe size={16} className="text-blue-400 shrink-0" />
                    <span className="text-xs font-bold text-white uppercase tracking-wider">Filter Analysis By Page URL:</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={selectedPageFilter}
                      onChange={(e) => setSelectedPageFilter(e.target.value)}
                      className="bg-black/60 border border-gray-700 text-blue-300 font-mono text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500 max-w-xs sm:max-w-md truncate cursor-pointer font-semibold shadow-inner"
                    >
                      <option value="ALL">🌐 All Audited Pages ({fallbackUrls.length ? fallbackUrls.length : 'All Sites'})</option>
                      {fallbackUrls.map((p, idx) => (
                        <option key={idx} value={p.url}>
                          📄 {p.url.replace(selectedProject.url || '', '') || '/'} {p.page_info?.title ? `(${p.page_info.title})` : ''}
                        </option>
                      ))}
                    </select>

                    {selectedPageFilter !== 'ALL' && (
                      <button
                        onClick={() => setSelectedPageFilter('ALL')}
                        className="text-[10px] text-gray-400 hover:text-white bg-gray-800 px-2 py-1.5 rounded-lg border border-gray-700 font-mono transition"
                      >
                        Clear Filter
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* TAB CONTENTS */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl min-h-[400px]">

              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="flex flex-col gap-6 animate-in fade-in duration-300">

                  {/* High level Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col justify-between gap-2 shadow-inner">
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Robots.txt</span>
                        <span className="text-sm font-extrabold text-white flex items-center gap-1.5 mt-1">
                          {(() => {
                            const status = selectedProject.robots_status || selectedProject.crawl_overview?.robots_txt_status || 'Missing';
                            const isSuccess = status.includes('Found') || status.includes('Present') || status.includes('Valid');
                            return (
                              <>
                                {isSuccess ? <CheckCircle size={14} className="text-green-500" /> : <ShieldAlert size={14} className="text-red-500" />}
                                <span className={isSuccess ? 'text-green-400' : 'text-red-400'}>{status}</span>
                              </>
                            );
                          })()}
                        </span>
                      </div>
                      {!(selectedProject.robots_status || selectedProject.crawl_overview?.robots_txt_status || '').includes('Found') && (
                        <button
                          onClick={() => handleOpenQuickFix('robots')}
                          className="mt-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-purple-600/30 to-blue-600/30 hover:from-purple-600/50 hover:to-blue-600/50 border border-purple-500/40 text-purple-200 text-xs font-bold py-1.5 px-3 rounded-lg transition shadow-sm"
                        >
                          <Sparkles size={12} className="text-purple-300 animate-pulse" /> 1-Click Fix
                        </button>
                      )}
                    </div>

                    <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col justify-between gap-2 shadow-inner">
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">XML Sitemap</span>
                        <span className="text-sm font-extrabold text-white flex items-center gap-1.5 mt-1">
                          {(() => {
                            const status = selectedProject.sitemap_status || selectedProject.crawl_overview?.sitemap_status || 'Missing';
                            const isSuccess = status.includes('Found') || status.includes('Present') || status.includes('Valid');
                            return (
                              <>
                                {isSuccess ? <CheckCircle size={14} className="text-green-500" /> : <ShieldAlert size={14} className="text-red-500" />}
                                <span className={isSuccess ? 'text-green-400' : 'text-red-400'}>{status}</span>
                              </>
                            );
                          })()}
                        </span>
                      </div>
                      {!(selectedProject.sitemap_status || selectedProject.crawl_overview?.sitemap_status || '').includes('Found') && (
                        <button
                          onClick={() => handleOpenQuickFix('sitemap')}
                          className="mt-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-purple-600/30 to-blue-600/30 hover:from-purple-600/50 hover:to-blue-600/50 border border-purple-500/40 text-purple-200 text-xs font-bold py-1.5 px-3 rounded-lg transition shadow-sm"
                        >
                          <Sparkles size={12} className="text-purple-300 animate-pulse" /> 1-Click Fix
                        </button>
                      )}
                    </div>

                    <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1 shadow-inner">
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Pages Crawled</span>
                      <span className="text-sm font-extrabold text-white mt-1">
                        {selectedProject.pages_analyzed ?? selectedProject.crawl_overview?.analyzed_pages ?? 0} analyzed /{' '}
                        {selectedProject.pages_discovered ?? selectedProject.crawl_overview?.discovered_internal_pages ?? 0} found
                      </span>
                    </div>

                    <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1 shadow-inner">
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Domain Authority</span>
                      <span className="text-sm font-extrabold text-white mt-1">
                        {selectedProject.crawl_overview?.domain_authority ?? '35'} / 100
                      </span>
                    </div>
                  </div>

                  {/* Executive Summary */}
                  {selectedProject.executive_summary && (
                    <div className="bg-black/30 border border-gray-850 p-5 rounded-2xl flex flex-col gap-2">
                      <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
                        <Info size={15} className="text-blue-500" />
                        Executive Summary
                      </h4>
                      <p className="text-xs text-gray-300 leading-relaxed font-sans whitespace-pre-line">
                        {selectedProject.executive_summary}
                      </p>
                    </div>
                  )}

                  {/* Sampled Pages List */}
                  {selectedProject.crawl_overview?.sampled_pages && selectedProject.crawl_overview.sampled_pages.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
                        <Link2 size={15} className="text-indigo-400" />
                        Sampled Page Audits
                      </h4>

                      <div className="border border-gray-800/80 rounded-xl overflow-hidden bg-black/20">
                        <table className="w-full text-left border-collapse text-[11px]">
                          <thead>
                            <tr className="bg-gray-950/70 border-b border-gray-800 text-gray-400">
                              <th className="p-3 font-bold">Relative Path</th>
                              <th className="p-3 font-bold text-center">Status</th>
                              <th className="p-3 font-bold text-center">H1</th>
                              <th className="p-3 font-bold text-center">H2</th>
                              <th className="p-3 font-bold text-center">H3</th>
                              <th className="p-3 font-bold text-center">H4–H6</th>
                              <th className="p-3 font-bold">Primary H1 Title</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-850">
                            {(selectedPageFilter === 'ALL'
                              ? selectedProject.crawl_overview.sampled_pages
                              : selectedProject.crawl_overview.sampled_pages.filter(p => p.url === selectedPageFilter)
                            ).map((p, idx) => {
                              const h1C = p.h1_count ?? p.headings?.h1_count ?? (p.headings?.h1?.length ?? (p.h1_first || p.h1_content ? 1 : 0));
                              const h2C = p.headings?.h2?.length ?? p.headings?.h2_count ?? 0;
                              const h3C = p.headings?.h3?.length ?? p.headings?.h3_count ?? 0;
                              const h4C = p.headings?.h4?.length ?? p.headings?.h4_count ?? 0;
                              const h5C = p.headings?.h5?.length ?? p.headings?.h5_count ?? 0;
                              const h6C = p.headings?.h6?.length ?? p.headings?.h6_count ?? 0;
                              const h46C = h4C + h5C + h6C;

                              return (
                                <tr key={idx} className="hover:bg-gray-800/35 transition-colors">
                                  <td className="p-3 font-mono text-gray-300 truncate max-w-[180px]" title={p.url}>
                                    {p.url.replace(selectedProject.url, '') || '/'}
                                  </td>
                                  <td className="p-3 text-center">
                                    {(() => {
                                      const code = p.status_code ?? p.page_info?.status_code ?? 200;
                                      const is200 = Number(code) === 200 || code === 200 || code === '200';
                                      return (
                                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${is200
                                          ? 'bg-green-950/40 text-green-400 border border-green-500/20'
                                          : 'bg-red-950/40 text-red-400 border border-red-500/20'
                                          }`}>
                                          {code}
                                        </span>
                                      );
                                    })()}
                                  </td>

                                  {/* H1 Checkbox Column */}
                                  <td className="p-3 text-center">
                                    <input
                                      type="checkbox"
                                      readOnly
                                      checked={h1C === 1}
                                      className={`w-4 h-4 rounded border cursor-default accent-blue-600 ${h1C === 1 ? 'border-blue-500 text-blue-600' : 'border-gray-700 bg-gray-900'
                                        }`}
                                      title={h1C === 1 ? 'Valid H1 Present' : `H1 Count: ${h1C}`}
                                    />
                                  </td>

                                  {/* H2 Checkbox Column */}
                                  <td className="p-3 text-center">
                                    <input
                                      type="checkbox"
                                      readOnly
                                      checked={h2C > 0}
                                      className={`w-4 h-4 rounded border cursor-default accent-blue-600 ${h2C > 0 ? 'border-blue-500 text-blue-600' : 'border-gray-700 bg-gray-900'
                                        }`}
                                      title={`H2 Count: ${h2C}`}
                                    />
                                  </td>

                                  {/* H3 Checkbox Column */}
                                  <td className="p-3 text-center">
                                    <input
                                      type="checkbox"
                                      readOnly
                                      checked={h3C > 0}
                                      className={`w-4 h-4 rounded border cursor-default accent-blue-600 ${h3C > 0 ? 'border-blue-500 text-blue-600' : 'border-gray-700 bg-gray-900'
                                        }`}
                                      title={`H3 Count: ${h3C}`}
                                    />
                                  </td>

                                  {/* H4-H6 Checkbox Column */}
                                  <td className="p-3 text-center">
                                    <input
                                      type="checkbox"
                                      readOnly
                                      checked={h46C > 0}
                                      className={`w-4 h-4 rounded border cursor-default accent-blue-600 ${h46C > 0 ? 'border-blue-500 text-blue-600' : 'border-gray-700 bg-gray-900'
                                        }`}
                                      title={`H4-H6 Count: ${h46C}`}
                                    />
                                  </td>

                                  <td className="p-3 text-gray-400 truncate max-w-[250px]" title={p.h1_first || p.h1_content || p.headings?.h1_content || p.headings?.h1?.[0]}>
                                    {p.h1_first || p.h1_content || p.headings?.h1_content || p.headings?.h1?.[0] || (
                                      <span className="text-red-500/60 font-mono italic">None Found</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Competitor overlap */}
                  {selectedProject.competitive_intelligence && (
                    <div className="bg-black/30 border border-gray-850 p-5 rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col gap-2">
                        <h4 className="font-extrabold text-white text-sm">Competitive Intelligence</h4>
                        <div className="flex gap-4 mt-2">
                          <div className="flex flex-col">
                            <span className="text-[10px] text-gray-500">Overlap Score</span>
                            <span className="text-xl font-mono font-black text-indigo-400">
                              {selectedProject.competitive_intelligence.keyword_overlap_score || '15%'}
                            </span>
                          </div>
                          <div className="w-[1px] bg-gray-800"></div>
                          <div className="flex flex-col">
                            <span className="text-[10px] text-gray-500">Content Gap Ratio</span>
                            <span className="text-xl font-mono font-black text-purple-400">
                              {selectedProject.competitive_intelligence.content_gap_ratio || '74%'}
                            </span>
                          </div>
                          <div className="w-[1px] bg-gray-800"></div>
                          <div className="flex flex-col">
                            <span className="text-[10px] text-gray-500">Competitors Evaluated</span>
                            <span className="text-xl font-mono font-black text-white">
                              {selectedProject.competitive_intelligence.competitor_sample_size || '3'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Identified Competitor Candidates</span>
                          <span className="text-[9px] text-purple-400 font-mono">Select domains to evaluate</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {(() => {
                            const rawCompList = selectedProject.competitive_intelligence?.competitors || [];
                            if (rawCompList.length === 0) return <span className="text-xs text-gray-500 italic">No competitors discovered for this URL.</span>;
                            return rawCompList.map((c, i) => {
                              const domainStr = typeof c === 'string' ? c.replace(/^https?:\/\//i, '').replace(/\/$/, '') : (c.domain || c.url || `competitor-${i + 1}.com`).replace(/^https?:\/\//i, '').replace(/\/$/, '');
                              const isSelected = customCompetitorsList.includes(`https://${domainStr}`) || customCompetitorsList.length === 0;


                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => {
                                    const formatted = `https://${domainStr}`;
                                    if (customCompetitorsList.includes(formatted)) {
                                      setCustomCompetitorsList(prev => prev.filter(item => item !== formatted));
                                    } else {
                                      setCustomCompetitorsList(prev => [...prev, formatted]);
                                    }
                                  }}
                                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 border ${isSelected
                                    ? 'bg-purple-950/80 border-purple-500/60 text-purple-200 shadow-sm shadow-purple-900/30'
                                    : 'bg-black/50 border-gray-800 text-gray-400 hover:border-gray-700'
                                    }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => { }}
                                    className="w-3.5 h-3.5 rounded accent-purple-600 cursor-pointer"
                                  />
                                  <Globe size={13} className={isSelected ? "text-purple-400" : "text-gray-500"} />
                                  <span>{domainStr}</span>
                                  <span className="text-[10px] text-gray-500 font-mono font-bold bg-black/60 px-1.5 py-0.2 rounded border border-gray-800">
                                    #{i + 1}
                                  </span>
                                </button>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  )}


                </div>
              )}

              {/* TECHNICAL FINDINGS TAB */}
              {activeTab === 'findings' && (
                <div className="flex flex-col gap-4 animate-in fade-in duration-300">

                  {/* Page-Wise Technical Breakdown */}
                  {selectedProject.technical_audit?.page_summaries && selectedProject.technical_audit.page_summaries.length > 0 && (
                    <div className="flex flex-col gap-3 mb-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
                          <Globe size={15} className="text-blue-400" />
                          Page-Wise Technical Audit Matrix
                        </h4>
                        <span className="text-[10px] text-gray-500 font-mono">Sampled Pages Scraped: {selectedProject.technical_audit.page_summaries.length}</span>
                      </div>

                      <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto custom-scrollbar">
                        {selectedProject.technical_audit.page_summaries.map((page, pIdx) => (
                          <div key={pIdx} className="bg-black/25 border border-gray-850 p-3.5 rounded-xl flex flex-col md:flex-row justify-between md:items-center gap-3">
                            <div className="flex flex-col gap-1 truncate max-w-md">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase font-mono ${page.page_info?.indexing_status === 'Indexable' ? 'bg-green-950/50 text-green-400 border border-green-500/20' : 'bg-red-950/50 text-red-400 border border-red-500/20'}`}>
                                  {page.page_info?.indexing_status || 'Indexable'}
                                </span>
                                <span className="font-mono text-xs font-bold text-blue-300 truncate">{page.url}</span>
                              </div>
                              <span className="text-[10px] text-gray-400 truncate">Title: {page.page_info?.title}</span>
                            </div>

                            <div className="flex items-center gap-4 text-[10px] font-mono shrink-0">
                              <div className="flex flex-col items-end">
                                <span className="text-gray-500">H1 Headings</span>
                                <span className={page.headings?.h1_count === 1 ? 'text-green-400 font-bold' : 'text-yellow-400 font-bold'}>
                                  {page.headings?.h1_count ?? 0} H1
                                </span>
                              </div>
                              <div className="w-[1px] h-6 bg-gray-850"></div>
                              <div className="flex flex-col items-end">
                                <span className="text-gray-500">Word Depth</span>
                                <span className={(page.content?.word_count ?? 0) >= 300 ? 'text-green-400 font-bold' : 'text-amber-400 font-bold'}>
                                  {page.content?.word_count ?? 0} words
                                </span>
                              </div>
                              <div className="w-[1px] h-6 bg-gray-850"></div>
                              <div className="flex flex-col items-end">
                                <span className="text-gray-500">SEO Health</span>
                                <span className="text-indigo-400 font-extrabold">{page.seo_score ?? 80}/100</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-extrabold text-white text-sm">Core Audit Findings</h4>
                    <span className="text-xs text-gray-500">Click a finding row to view suggestions and generate AI fixes.</span>
                  </div>

                  <div className="flex flex-col gap-3">
                    {(() => {
                      const allFindings = selectedProject.technical_audit?.findings || [];
                      const filteredFindings = selectedPageFilter === 'ALL'
                        ? allFindings
                        : allFindings.filter(f => f.evidence && f.evidence.some(e => e.url === selectedPageFilter));

                      if (filteredFindings.length === 0) {
                        return (
                          <div className="text-center py-12 text-gray-600 bg-black/20 rounded-xl border border-gray-850 text-sm">
                            {selectedPageFilter === 'ALL'
                              ? '🎉 Splendid! No technical issues detected on the sampled URLs.'
                              : `🎉 No technical issues detected for URL: ${selectedPageFilter}`}
                          </div>
                        );
                      }

                      return filteredFindings.map((f, idx) => {
                        const isExpanded = expandedFinding === idx;
                        const fix = fixes[idx];

                        return (
                          <div
                            key={idx}
                            className={`border rounded-xl transition-all overflow-hidden ${isExpanded ? 'bg-black/35 border-blue-500/40' : 'bg-black/20 border-gray-800 hover:border-gray-700'
                              }`}
                          >
                            {/* Header row */}
                            <div
                              onClick={() => setExpandedFinding(isExpanded ? null : idx)}
                              className="p-4 flex items-center justify-between cursor-pointer select-none"
                            >
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase border tracking-wider shrink-0 ${getPriorityColor(f.priority)}`}>
                                  {f.priority || 'Medium'}
                                </span>
                                <span className="font-bold text-xs text-gray-200 truncate max-w-[200px] sm:max-w-md">
                                  {f.metric}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                {f.evidence && f.evidence.length > 0 && (
                                  <span className="text-[10px] text-amber-300 font-mono font-bold bg-amber-950/60 border border-amber-500/30 px-2 py-0.5 rounded">
                                    {f.evidence.length} {f.evidence.length === 1 ? 'Page Affected' : 'Pages Affected'}
                                  </span>
                                )}
                                <span className="text-[10px] text-gray-500 font-mono hidden md:inline">
                                  {f.category}
                                </span>
                                {isExpanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                              </div>
                            </div>

                            {/* Expanded details */}
                            {isExpanded && (
                              <div className="px-4 pb-5 pt-1 border-t border-gray-850/65 flex flex-col gap-4 text-xs">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Business Impact</span>
                                    <p className="text-gray-300 leading-relaxed font-sans">{f.business_impact}</p>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Recommendation</span>
                                    <p className="text-blue-200 leading-relaxed font-sans font-medium">{f.recommendation}</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-850/40 pt-3">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Observed Value</span>
                                    <p className="font-mono text-gray-300 text-[11px] mt-0.5">{f.current_value}</p>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">SEO Benchmark</span>
                                    <p className="font-mono text-gray-400 text-[11px] mt-0.5">{f.benchmark}</p>
                                  </div>
                                </div>

                                {/* Evidence Points */}
                                {f.evidence && f.evidence.length > 0 && (
                                  <div className="flex flex-col gap-1 border-t border-gray-850/40 pt-3">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold flex items-center gap-1">
                                        <Globe size={11} className="text-amber-400" />
                                        Affected Pages & Observed Errors ({f.evidence.length} URLs)
                                      </span>
                                    </div>
                                    <div className="flex flex-col gap-1.5 mt-1 max-h-36 overflow-y-auto custom-scrollbar">
                                      {f.evidence.map((ev, eIdx) => (
                                        <div key={eIdx} className="bg-gray-950 p-2.5 rounded-lg border border-gray-850 flex flex-col sm:flex-row justify-between text-[11px] gap-2 items-start sm:items-center">
                                          <a
                                            href={ev.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-mono text-blue-400 hover:text-blue-300 underline truncate max-w-full sm:max-w-md font-semibold"
                                            title={ev.url}
                                          >
                                            {ev.url}
                                          </a>
                                          <span className="text-amber-300/90 text-xs bg-amber-950/40 border border-amber-500/20 px-2 py-0.5 rounded font-mono">
                                            {ev.observation}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* AI Fix section */}
                                <div className="border-t border-gray-850/40 pt-4 flex flex-col gap-3">
                                  <div className="flex justify-between items-center">
                                    <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold flex items-center gap-1.5">
                                      <Sparkles size={11} className="text-purple-400" />
                                      AI-Assisted Code Correction
                                    </span>
                                    {!fix && (
                                      <button
                                        onClick={() => handleGenerateFix(f, idx)}
                                        disabled={generatingFixId !== null}
                                        className="bg-purple-900/40 hover:bg-purple-900/60 border border-purple-500/30 text-purple-300 font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all text-[10px] disabled:opacity-50"
                                      >
                                        {generatingFixId === idx ? 'Querying Agent...' : '✨ Generate Code Fix'}
                                      </button>
                                    )}
                                  </div>

                                  {generatingFixId === idx && (
                                    <div className="bg-black/50 p-6 rounded-lg border border-purple-500/20 text-center animate-pulse text-purple-300 font-mono text-[11px]">
                                      ⚙️ Compiling code structures and executing fixing engine...
                                    </div>
                                  )}

                                  {fix && (
                                    <div className="bg-black/60 rounded-xl border border-gray-850 p-4 flex flex-col gap-3 animate-in fade-in duration-300">
                                      {fix.explanation && (
                                        <div className="text-xs text-gray-300 font-sans italic bg-purple-950/15 border border-purple-500/10 p-2.5 rounded-lg">
                                          {fix.explanation}
                                        </div>
                                      )}

                                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-1.5">
                                          <span className="text-[9px] text-red-400 font-bold uppercase tracking-wide">Current Problematic HTML/CSS</span>
                                          <div className="relative">
                                            <pre className="bg-gray-950 p-3 rounded-lg border border-red-500/10 font-mono text-[10px] text-red-200 overflow-x-auto max-h-48 custom-scrollbar">
                                              {fix.current_code}
                                            </pre>
                                          </div>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                          <div className="flex justify-between items-center">
                                            <span className="text-[9px] text-green-400 font-bold uppercase tracking-wide">Corrected SEO-Optimized Code</span>
                                            <button
                                              onClick={() => handleCopyCode(fix.fixed_code, idx)}
                                              className="text-[9px] text-gray-400 hover:text-white flex items-center gap-1 bg-gray-850 px-2 py-0.5 rounded border border-gray-800 transition"
                                            >
                                              {copiedFixId === idx ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
                                              {copiedFixId === idx ? 'Copied!' : 'Copy Code'}
                                            </button>
                                          </div>
                                          <pre className="bg-gray-950 p-3 rounded-lg border border-green-500/20 font-mono text-[10px] text-green-200 overflow-x-auto max-h-48 custom-scrollbar">
                                            {fix.fixed_code}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                </div>

                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* ON-PAGE SEO TAB */}
              {activeTab === 'onpage' && (
                <div className="flex flex-col gap-6 animate-in fade-in duration-300">

                  {/* AI Meta Tag Generator */}
                  <AiMetaGenerator
                    project={selectedProject}
                    apiFetch={apiFetch}
                    showNotification={showNotification}
                  />

                  {/* On-Page Score Card Header */}
                  <div className="bg-black/35 border border-gray-850 p-6 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <FileCode size={18} className="text-blue-400" />
                        <h4 className="font-extrabold text-white text-base">On-Page SEO Benchmark Audit</h4>
                      </div>
                      <p className="text-xs text-gray-400">
                        Evaluated using Ubersuggest On-Page SEO model: <span className="font-mono text-gray-300">100 - (0.35 × % Dup Titles) - (0.35 × % Dup Metas) - (0.35 × % Thin Content)</span>
                      </p>
                    </div>

                    <div className="flex items-center gap-4 bg-gray-950 p-3.5 rounded-xl border border-gray-800 shrink-0">
                      <div className="flex flex-col items-end font-mono">
                        <span className="text-[10px] text-gray-500 uppercase font-bold">On-Page Score</span>
                        <span className="text-2xl font-black text-blue-400">
                          {selectedProject.crawl_overview?.onpage_score ?? selectedProject.onpage_score ?? 68}/100
                        </span>
                      </div>
                      <div className="w-[1px] h-8 bg-gray-800"></div>
                      <div className="flex flex-col items-end font-mono">
                        <span className="text-[10px] text-gray-500 uppercase font-bold">Status</span>
                        <span className="text-xs font-bold text-green-400">
                          {(selectedProject.crawl_overview?.onpage_score ?? selectedProject.onpage_score ?? 68) >= 70 ? 'Excellent' : 'Needs Optimization'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* On-Page Core Pillar Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-black/25 border border-gray-850 p-4 rounded-xl flex flex-col gap-1.5">
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Title Tag Health</span>
                      <span className="text-lg font-bold text-white font-mono">
                        {selectedProject.crawl_overview?.sampled_pages?.filter(p => p.h1_count >= 1).length ?? 0} / {selectedProject.crawl_overview?.sampled_pages?.length ?? 0} Pages Valid
                      </span>
                      <span className="text-[11px] text-gray-400">Deduction: <span className="text-amber-400 font-mono">-0.35% per duplicate</span></span>
                    </div>

                    <div className="bg-black/25 border border-gray-850 p-4 rounded-xl flex flex-col gap-1.5">
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Meta Description Health</span>
                      <span className="text-lg font-bold text-white font-mono">
                        {selectedProject.crawl_overview?.sampled_pages?.filter(p => p.status_code === 200).length ?? 0} / {selectedProject.crawl_overview?.sampled_pages?.length ?? 0} Pages Analyzed
                      </span>
                      <span className="text-[11px] text-gray-400">Deduction: <span className="text-amber-400 font-mono">-0.35% per missing description</span></span>
                    </div>

                    <div className="bg-black/25 border border-gray-850 p-4 rounded-xl flex flex-col gap-1.5">
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Content Word Depth</span>
                      <span className="text-lg font-bold text-white font-mono">
                        300+ Words Benchmark
                      </span>
                      <span className="text-[11px] text-gray-400">Deduction: <span className="text-amber-400 font-mono">-0.35% per thin content page</span></span>
                    </div>
                  </div>

                  {/* Page-by-Page On-Page Audit List */}
                  {selectedProject.technical_audit?.page_summaries && selectedProject.technical_audit.page_summaries.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
                        <Globe size={15} className="text-blue-400" />
                        Page-by-Page On-Page Parameter Verification
                      </h4>

                      <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto custom-scrollbar">
                        {(() => {
                          const pages = selectedProject.technical_audit.page_summaries;
                          const filteredPages = selectedPageFilter === 'ALL' ? pages : pages.filter(p => p.url === selectedPageFilter);

                          return filteredPages.map((page, pIdx) => (
                            <div key={pIdx} className="bg-black/25 border border-gray-850 p-4 rounded-xl flex flex-col md:flex-row justify-between md:items-center gap-4">
                              <div className="flex flex-col gap-1 truncate max-w-md">
                                <span className="font-mono text-xs font-bold text-blue-300 truncate">{page.url}</span>
                                <span className="text-[11px] text-gray-300 truncate">Title: {page.page_info?.title || 'No Title Specified'}</span>
                                <span className="text-[10px] text-gray-500 truncate">H1: {page.headings?.h1_content || 'Missing H1 Tag'}</span>
                              </div>

                              <div className="flex items-center gap-4 text-[10px] font-mono shrink-0">
                                <div className="flex flex-col items-end">
                                  <span className="text-gray-500">Status Code</span>
                                  <span className={(Number(page.page_info?.status_code ?? page.status_code ?? 200) === 200) ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                                    {page.page_info?.status_code || page.status_code || 200} OK
                                  </span>
                                </div>
                                <div className="w-[1px] h-6 bg-gray-850"></div>
                                <div className="flex flex-col items-end">
                                  <span className="text-gray-500">Word Count</span>
                                  <span className={(page.content?.word_count ?? 0) >= 300 ? 'text-green-400 font-bold' : 'text-amber-400 font-bold'}>
                                    {page.content?.word_count ?? 0} words
                                  </span>
                                </div>
                                <div className="w-[1px] h-6 bg-gray-850"></div>
                                <div className="flex flex-col items-end">
                                  <span className="text-gray-500">On-Page Score</span>
                                  <span className="text-blue-400 font-extrabold">{page.seo_score ?? 80}/100</span>
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* KEYWORDS AND CONTENT STRATEGY */}
              {activeTab === 'content' && (
                <div className="flex flex-col gap-6 animate-in fade-in duration-300">

                  {/* AI Content Expansion Outline */}
                  <AiContentOutline
                    project={selectedProject}
                    apiFetch={apiFetch}
                    showNotification={showNotification}
                  />

                  {/* Page-Wise Target Keyword Mapping Matrix */}
                  {selectedProject.technical_audit?.page_summaries && selectedProject.technical_audit.page_summaries.length > 0 && (
                    <div className="flex flex-col gap-3 bg-black/35 border border-purple-500/30 p-5 rounded-2xl">
                      <div className="flex justify-between items-center">
                        <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
                          <FileText size={15} className="text-purple-400" />
                          Page-Wise Target Keyword & Content Strategy Mapping
                        </h4>
                        <span className="text-[10px] text-purple-300 font-mono bg-purple-950/60 border border-purple-500/40 px-2.5 py-1 rounded-lg">URL Mapping Matrix</span>
                      </div>

                      <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto custom-scrollbar mt-1">
                        {(() => {
                          const pages = selectedProject.technical_audit.page_summaries;
                          const filteredPages = selectedPageFilter === 'ALL'
                            ? pages
                            : pages.filter(p => p.url === selectedPageFilter);

                          return filteredPages.map((p, idx) => {
                            const kwList = selectedProject.keyword_analysis?.primary_keywords || [];
                            const targetKw = typeof kwList[idx % kwList.length] === 'object' ? (kwList[idx % kwList.length]?.keyword || kwList[idx % kwList.length]?.name) : kwList[idx % kwList.length] || 'Primary SEO Topic';

                            return (
                              <div key={idx} className="bg-gray-950/80 border border-gray-850 p-3.5 rounded-xl flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                                <div className="flex flex-col gap-0.5 truncate max-w-sm sm:max-w-md">
                                  <span className="text-xs font-bold text-blue-300 font-mono truncate">{p.url}</span>
                                  <span className="text-[10px] text-gray-400 truncate">H1: {p.headings?.h1_content || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <span className="text-[10px] font-mono text-purple-300 bg-purple-950/60 border border-purple-500/30 px-2.5 py-1 rounded-lg">
                                    Target Keyword: <span className="font-bold text-white">{targetKw}</span>
                                  </span>
                                  <span className="text-[10px] font-mono text-gray-400">
                                    {p.content?.quality || 'Average'} Quality
                                  </span>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Keyword analysis details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-black/35 border border-gray-850 p-5 rounded-2xl flex flex-col gap-3">
                      <h4 className="font-extrabold text-white text-sm">Primary Scraped Keywords</h4>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {selectedProject.keyword_analysis?.primary_keywords?.map((kw, i) => (
                          <span key={i} className="px-2.5 py-1 rounded-full bg-blue-950/50 text-blue-400 border border-blue-500/20 text-xs font-mono font-medium">
                            {kw.keyword} (Density: {kw.density || '2.4%'} · Vol: {kw.search_volume || '1.2k'})
                          </span>
                        )) || (
                            <span className="text-xs text-gray-600 italic">No keyword density evaluated.</span>
                          )}
                      </div>
                    </div>

                    <div className="bg-black/35 border border-gray-850 p-5 rounded-2xl flex flex-col gap-3">
                      <h4 className="font-extrabold text-white text-sm">Target Long-Tail Keywords</h4>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {selectedProject.keyword_analysis?.long_tail_keywords?.map((kw, i) => (
                          <span key={i} className="px-2.5 py-1 rounded-full bg-purple-950/50 text-purple-400 border border-purple-500/20 text-xs font-mono font-medium">
                            {kw.keyword || kw}
                          </span>
                        )) || (
                            <span className="text-xs text-gray-600 italic">No long-tail keyword clusters suggested.</span>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Blog ideas */}
                  <div className="flex flex-col gap-3">
                    <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
                      <FileSpreadsheet size={15} className="text-green-400" />
                      AI Content Roadmap Suggestions
                    </h4>

                    {(!selectedProject.content_strategy?.blog_suggestions || selectedProject.content_strategy.blog_suggestions.length === 0) ? (
                      <div className="text-center py-6 text-gray-600 italic text-xs">
                        No content suggestions generated for this industry.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedProject.content_strategy.blog_suggestions.map((post, idx) => (
                          <div key={idx} className="bg-black/20 border border-gray-850 rounded-xl p-4 flex flex-col gap-2 relative">
                            <span className="absolute top-4 right-4 text-[9px] font-mono text-gray-600">ID: #{idx + 1}</span>
                            <span className="text-[9px] uppercase font-bold tracking-wider text-green-400">Blog Article Spec</span>
                            <h5 className="font-bold text-white text-xs leading-snug mt-1">{post.title}</h5>

                            <div className="flex flex-col gap-1.5 mt-2 text-[11px] border-t border-gray-850/50 pt-2 text-gray-400">
                              <div>
                                <span className="text-gray-500 font-bold">Focus Keywords:</span>{' '}
                                <span className="font-mono text-gray-300">{post.keywords || post.focus_keywords || 'SEO Strategy'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 font-bold">Audience Intent:</span>{' '}
                                <span className="text-gray-300">{post.intent || 'Informational'}</span>
                              </div>
                              <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">{post.description || post.outline}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Guest post ideas */}
                  {selectedProject.content_strategy?.guest_post_titles && selectedProject.content_strategy.guest_post_titles.length > 0 && (
                    <div className="bg-black/20 border border-gray-850 p-5 rounded-2xl flex flex-col gap-3">
                      <h4 className="font-extrabold text-white text-sm">Guest Post Pitch Headlines (Authority Building)</h4>
                      <div className="flex flex-col gap-2">
                        {selectedProject.content_strategy.guest_post_titles.map((title, i) => (
                          <div key={i} className="flex items-center gap-2.5 text-xs text-gray-300">
                            <ChevronRight size={12} className="text-purple-400 shrink-0" />
                            <span>{title}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* PERFORMANCE & LINKS TAB */}
              {activeTab === 'performance' && (
                <div className="flex flex-col gap-6 animate-in fade-in duration-300">

                  {/* Speed Metrics */}
                  <div className="bg-black/20 border border-gray-850 p-5 rounded-2xl flex flex-col gap-4">
                    <h4 className="font-extrabold text-white text-sm">Page Speed & Web Vitals Audit</h4>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
                      <div className="bg-gray-950 p-3.5 rounded-xl border border-gray-850 flex flex-col gap-1 text-center">
                        <span className="text-[9px] text-gray-500 uppercase tracking-widest font-bold">LCP</span>
                        <span className="text-base font-mono font-black text-green-400 mt-0.5">
                          {selectedProject.page_speed?.lcp || '1.2s'}
                        </span>
                        <span className="text-[9px] text-gray-600">Largest Contentful Paint</span>
                      </div>
                      <div className="bg-gray-950 p-3.5 rounded-xl border border-gray-850 flex flex-col gap-1 text-center">
                        <span className="text-[9px] text-gray-500 uppercase tracking-widest font-bold">FID</span>
                        <span className="text-base font-mono font-black text-green-400 mt-0.5">
                          {selectedProject.page_speed?.fid || '14ms'}
                        </span>
                        <span className="text-[9px] text-gray-600">First Input Delay</span>
                      </div>
                      <div className="bg-gray-950 p-3.5 rounded-xl border border-gray-850 flex flex-col gap-1 text-center">
                        <span className="text-[9px] text-gray-500 uppercase tracking-widest font-bold">CLS</span>
                        <span className="text-base font-mono font-black text-green-400 mt-0.5">
                          {selectedProject.page_speed?.cls || '0.02'}
                        </span>
                        <span className="text-[9px] text-gray-600">Cumulative Layout Shift</span>
                      </div>
                      <div className="bg-gray-950 p-3.5 rounded-xl border border-gray-850 flex flex-col gap-1 text-center">
                        <span className="text-[9px] text-gray-500 uppercase tracking-widest font-bold">Speed Score</span>
                        <span className="text-base font-mono font-black text-yellow-400 mt-0.5">
                          {selectedProject.page_speed?.score || '88'} / 100
                        </span>
                        <span className="text-[9px] text-gray-600">Mobile Speed Audit</span>
                      </div>
                    </div>
                  </div>

                  {/* Internal external link audit */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-black/20 border border-gray-850 p-5 rounded-2xl flex flex-col gap-3">
                      <h4 className="font-extrabold text-white text-sm">Internal Linking Structure</h4>
                      <div className="flex flex-col gap-2 text-xs text-gray-400">
                        <div className="flex justify-between border-b border-gray-850/50 pb-2">
                          <span>Broken Link Ratio:</span>
                          <span className="font-mono font-bold text-green-400">
                            {selectedProject.crawl_overview?.broken_internal_link_ratio || '0.00%'}
                          </span>
                        </div>
                        <div className="flex justify-between border-b border-gray-850/50 pb-2">
                          <span>Crawler Depth:</span>
                          <span className="font-mono font-bold text-white">
                            {selectedProject.crawl_overview?.crawl_depth || '2'} levels
                          </span>
                        </div>
                        <div className="flex justify-between pb-2">
                          <span>Sample Coverage Ratio:</span>
                          <span className="font-mono font-bold text-indigo-400">
                            {selectedProject.crawl_overview?.sample_coverage_ratio || '100.00%'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-black/20 border border-gray-850 p-5 rounded-2xl flex flex-col gap-3">
                      <h4 className="font-extrabold text-white text-sm">Link Quality metrics</h4>
                      <div className="flex flex-col gap-2 text-xs text-gray-400">
                        <div className="flex justify-between border-b border-gray-850/50 pb-2">
                          <span>Estimated Backlinks:</span>
                          <span className="font-mono font-bold text-white">
                            {selectedProject.link_analysis?.estimated_backlink_count || '1,450'}
                          </span>
                        </div>
                        <div className="flex justify-between border-b border-gray-850/50 pb-2">
                          <span>Referring Domains:</span>
                          <span className="font-mono font-bold text-white">
                            {selectedProject.link_analysis?.referring_domains_count || '180'}
                          </span>
                        </div>
                        <div className="flex justify-between pb-2">
                          <span>Anchor Text Quality:</span>
                          <span className="font-mono font-bold text-green-400">Excellent (Relevant)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* ROADMAP TIMELINE (PAGE-WISE ROADMAP) */}
              {activeTab === 'roadmap' && (
                <div className="flex flex-col gap-6 animate-in fade-in duration-300">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-extrabold text-white text-sm">Page-Wise Action Roadmap</h4>
                      <p className="text-xs text-gray-500 mt-0.5">Custom roadmap items structured page-by-page across your crawled URLs.</p>
                    </div>
                    <span className="text-[10px] text-blue-400 font-mono bg-blue-950/40 border border-blue-500/20 px-2.5 py-1 rounded-lg">Page-Level Guidance</span>
                  </div>

                  {(!selectedProject.recommended_roadmap || selectedProject.recommended_roadmap.length === 0) ? (
                    <div className="text-center py-12 text-gray-600 italic text-xs">
                      No roadmap milestones generated for this site.
                    </div>
                  ) : (
                    <div className="relative pl-6 border-l border-gray-800 flex flex-col gap-6 mt-2 ml-2">
                      {selectedProject.recommended_roadmap.map((step, idx) => (
                        <div key={idx} className="relative flex flex-col gap-3">
                          {/* Circle indicator */}
                          <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-gray-900 border-2 border-blue-500 flex items-center justify-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                          </div>

                          <div className="bg-black/20 border border-gray-850 p-5 rounded-2xl flex flex-col gap-3">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-[10px] uppercase font-mono font-bold text-blue-400 bg-blue-950/40 border border-blue-500/20 px-2 py-0.5 rounded">
                                Phase {idx + 1}: {step.phase || step.priority || 'Action Step'} ({step.timeline})
                              </span>
                              <span className="text-[9px] text-gray-500 font-mono">Milestone #{idx + 1}</span>
                            </div>
                            <h5 className="font-extrabold text-white text-sm mt-0.5">{step.action_item || step.metric || 'SEO Step'}</h5>
                            <p className="text-[11px] text-gray-400 leading-relaxed font-sans">{step.details || step.business_impact || step.recommendation}</p>

                            {/* Page-by-Page Target List */}
                            {step.page_actions && step.page_actions.length > 0 && (
                              <div className="flex flex-col gap-2 mt-2 border-t border-gray-850/60 pt-3">
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                                  <Globe size={11} className="text-purple-400" />
                                  Target Pages & Specific Recommended Fixes
                                </span>

                                <div className="grid grid-cols-1 gap-2">
                                  {step.page_actions.map((pa, pIdx) => (
                                    <div key={pIdx} className="bg-gray-950/80 border border-gray-850 p-3 rounded-xl flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                                      <div className="flex flex-col gap-0.5 truncate max-w-sm sm:max-w-md">
                                        <span className="text-[11px] font-bold text-blue-300 font-mono truncate">{pa.page_url}</span>
                                        <span className="text-[10px] text-gray-500 truncate">{pa.title || 'Landing Page'}</span>
                                      </div>
                                      <div className="flex flex-col sm:items-end gap-1 shrink-0">
                                        <span className="text-[10px] text-green-400 font-semibold bg-green-950/30 border border-green-500/20 px-2 py-0.5 rounded">
                                          Fix: {pa.recommendation}
                                        </span>
                                        <span className="text-[9px] text-gray-500 font-mono">Target: {pa.target_keyword}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* LINK GRAPH TAB */}
              {activeTab === 'linkgraph' && (
                <LinkGraphPanel project={selectedProject} />
              )}

              {/* SCHEMA VALIDATOR TAB */}
              {activeTab === 'schema' && (
                <SchemaValidatorPanel project={selectedProject} selectedPageFilter={selectedPageFilter} />
              )}

              {/* IMAGES & ASSETS TAB */}
              {activeTab === 'images' && (
                <ImagesPanel project={selectedProject} selectedPageFilter={selectedPageFilter} />
              )}

              {/* KEYWORD CONFLICTS TAB */}
              {activeTab === 'conflicts' && (
                <KeywordConflictsPanel project={selectedProject} selectedPageFilter={selectedPageFilter} />
              )}

              {/* SERP PREVIEW TAB */}
              {activeTab === 'serp' && (
                <SerpPreviewPanel project={selectedProject} selectedPageFilter={selectedPageFilter} />
              )}

              {/* SCORE TRENDS TAB */}
              {activeTab === 'trends' && (
                <TrendsPanel project={selectedProject} apiFetch={apiFetch} />
              )}

              {/* AI CODE FIXES TAB */}
              {activeTab === 'aifixes' && (
                <AiFixesPanel project={selectedProject} apiFetch={apiFetch} showNotification={showNotification} />
              )}

            </div>
          </div>
        )}

        {/* QUICK FIX MODAL */}
        <AnimatePresence>
          {quickFixModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
              onClick={() => setQuickFixModal(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-gray-900 border border-gray-700/80 rounded-2xl max-w-2xl w-full p-6 shadow-2xl flex flex-col gap-4 text-left"
              >
                <div className="flex items-center justify-between border-b border-gray-800 pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-400">
                      <Sparkles size={18} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white">{quickFixModal.title}</h3>
                      <p className="text-xs text-gray-400 font-mono">Location: {quickFixModal.fileLocation}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setQuickFixModal(null)}
                    className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800 transition"
                  >
                    <X size={18} />
                  </button>
                </div>

                <p className="text-xs text-gray-300 leading-relaxed bg-black/30 p-3 rounded-xl border border-gray-800">
                  {quickFixModal.explanation}
                </p>

                <div className="relative">
                  <div className="flex items-center justify-between bg-gray-950 px-4 py-2 border-t border-x border-gray-800 rounded-t-xl text-[11px] text-gray-400 font-mono">
                    <span>{quickFixModal.filename}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(quickFixModal.code);
                        setCopiedModalCode(true);
                        setTimeout(() => setCopiedModalCode(false), 2000);
                      }}
                      className="flex items-center gap-1.5 text-purple-400 hover:text-purple-300 transition"
                    >
                      {copiedModalCode ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
                      <span>{copiedModalCode ? 'Copied!' : 'Copy Code'}</span>
                    </button>
                  </div>
                  <pre className="bg-gray-950/90 border border-gray-800 text-gray-200 font-mono text-xs p-4 rounded-b-xl overflow-x-auto max-h-60 leading-relaxed custom-scrollbar">
                    {quickFixModal.code}
                  </pre>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-800">
                  <span className="text-[11px] text-gray-400">
                    💡 Drop this file in your website's <code className="text-purple-300 bg-purple-950/40 px-1 py-0.5 rounded">public/</code> directory.
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQuickFixModal(null)}
                      className="px-3.5 py-2 rounded-xl text-xs font-semibold text-gray-400 hover:text-white hover:bg-gray-800 transition"
                    >
                      Close
                    </button>
                    <button
                      onClick={handleDownloadQuickFix}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-gray-300 bg-gray-800 hover:bg-gray-750 border border-gray-700 transition"
                    >
                      <Download size={13} /> Download
                    </button>
                    <button
                      onClick={handleApplyQuickFixToWebsite}
                      disabled={isApplyingFix}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 transition shadow-lg shadow-emerald-900/30"
                    >
                      {isApplyingFix ? (
                        <>
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Applying to Site...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} />
                          <span>🚀 Apply to Website</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default SEODashboard;
